import {GridColumns} from '@mui/x-data-grid';
import {Box, Typography} from '@mui/material';
import {FormattedMessage} from 'react-intl';
import {BusinessDTO} from 'src/types';
import {BusinessRowActions} from './BusinessRowActions';
import theme from 'src/theme';

/**
 * The columns of the business list.
 *
 * Only what the list endpoint actually returns: `GET /businesses` answers with `BusinessDTO`, which
 * carries no owner - that arrives only from the single-business endpoint. Columns for it would have
 * been permanently empty, which reads as missing data rather than as data that was never there.
 *
 * `activeId` is passed in rather than read from the store here: a column config describes a row, and
 * subscribing it to state would rebuild every column on each change.
 */
export const columnConfigBusinesses = (activeId: number): GridColumns => [
    {
        field: 'name',
        renderHeader: () => <FormattedMessage id="nameColumnHeader"/>,
        flex: 2,
        minWidth: 180,
        sortable: false,
        cellClassName: 'ampTableCell',
        renderCell: (params) => {
            const business = params.row as BusinessDTO;
            return (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0}}>
                    <Typography noWrap sx={{fontWeight: 600, fontSize: '0.875rem'}}>
                        {business.name}
                    </Typography>
                    {/* Which business the sidebar is pointing at, said on the screen where it is
                        chosen rather than only in the sidebar itself. */}
                    {business.id === activeId && (
                        <Typography
                            component="span"
                            sx={{
                                px: 0.75,
                                py: '1px',
                                borderRadius: '6px',
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                                letterSpacing: '0.03em',
                                textTransform: 'uppercase',
                                color: theme.palette.success.main,
                                backgroundColor: 'rgba(46, 125, 50, 0.12)',
                                flexShrink: 0,
                            }}
                        >
                            <FormattedMessage id="active"/>
                        </Typography>
                    )}
                </Box>
            );
        },
    },
    {
        field: 'address',
        renderHeader: () => <FormattedMessage id="addressColumnHeader"/>,
        flex: 3,
        minWidth: 200,
        sortable: false,
        cellClassName: 'ampTableCell',
        valueGetter: (params) => {
            const address = (params.row as BusinessDTO).address;
            if (!address) {
                return '-';
            }
            return [address.street, address.houseNumber, address.zipCode, address.city]
                .filter(Boolean)
                .join(' ') || '-';
        },
    },
    {
        field: 'uuid',
        renderHeader: () => <FormattedMessage id="uuid"/>,
        flex: 2,
        minWidth: 180,
        sortable: false,
        cellClassName: 'ampTableCell',
        valueGetter: (params) => (params.row as BusinessDTO).uuid ?? '-',
    },
    {
        field: 'userPerMonthCost',
        renderHeader: () => <FormattedMessage id="backendPricePerUser"/>,
        flex: 1.5,
        minWidth: 150,
        sortable: false,
        cellClassName: 'ampTableCell',
        valueGetter: (params) => {
            const business = params.row as BusinessDTO;
            return `${business.userPerMonthCost ?? 0} ${business.userPerMonthCurrency ?? ''}`.trim();
        },
    },
    {
        field: 'rfidCost',
        renderHeader: () => <FormattedMessage id="rfidCardPrice"/>,
        flex: 1.5,
        minWidth: 140,
        sortable: false,
        cellClassName: 'ampTableCell',
        // Stored in cents, the same way the RFID terms form writes it back.
        valueGetter: (params) => {
            const business = params.row as BusinessDTO;
            return `${(business.rfidCost ?? 0) / 100} ${business.rfidCostCurrency ?? ''}`.trim();
        },
    },
    {
        field: 'actions',
        renderHeader: () => null,
        width: 110,
        sortable: false,
        cellClassName: 'ampTableCell justifyEnd',
        renderCell: (params) => <BusinessRowActions businessId={(params.row as BusinessDTO).id}/>,
    },
];
